import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, Result, SectionTitle, Image, Separator, CorrectionBox, Note, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider } from '../../../../components/course/CourseComponents';
import screen2 from '../screen2.jpg';
import screen3 from '../screen3.jpg';
import screen4 from '../screen4.jpg';

const Chapitre2Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Affichage des tuyaux du haut et du bas, avec un espace entre les deux
        </p>
      </InfoBox>

      <Separator type="text" text="Chargement des images" color="gray-darker" size="md" />
      <SectionTitle>🚀 1/ Chargement des images</SectionTitle>

      <p>Dans Flappy Bird, des tuyaux se déplacent et le but est de passer entre ces tuyaux.</p>
      <p>Commençons par afficher 1 tuyau en haut et 1 en bas, en laissant un espace défini par la variable :</p>
      
      <CodeBlock language="javascript">
{`let tuyauEspace = 130;`}
      </CodeBlock>

      <p>On appellera ça une paire de tuyaux.</p>

      <Separator type="text" text="Structure des paires de tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🏗️ 2/ Structure de notre paire de tuyaux</SectionTitle>

      <p>Notre paire de tuyaux sera représentée par une structure avec les paramètres suivants :</p>

      <CodeBlock language="javascript">
{`let paireTuyaux = {
  x: 300,
  y: 100,
  franchi: false
};`}
      </CodeBlock>

      <ul className="list-disc list-inside mb-4">
        <li><InlineCode>franchi</InlineCode> est un booléen qui servira à compter le score. Il vaut <InlineCode>false</InlineCode> par défaut, et passe à <InlineCode>true</InlineCode> quand l'oiseau passe entre les deux tuyaux (on verra ça dans un prochain chapitre).</li>
        <li><InlineCode>x</InlineCode> détermine la position des deux tuyaux sur l'axe horizontal.
          <ul className="list-disc list-inside ml-4 mt-2">
            <li><InlineCode>x = 0</InlineCode> signifie que les tuyaux sont tout à gauche.</li>
            <li><InlineCode>x = width</InlineCode> signifie qu'ils sont tout à droite.</li>
            <li>Ici on utilise <InlineCode>x = 300</InlineCode> pour les placer au milieu.</li>
          </ul>
        </li>
      </ul>

      <Note type="warning" title="⚠️ Position verticale">
        <p><InlineCode>y</InlineCode> représente la position verticale du bas du tuyau haut (le début de l'espace entre les deux tuyaux) :</p>
        <ul className="list-disc list-inside">
          <li>Si <InlineCode>y = 0</InlineCode>, l'espace sera tout en haut. On ne verra pas le tuyau haut.</li>
          <li>Si <InlineCode>y = height</InlineCode>, on ne verra que le tuyau haut.</li>
        </ul>
      </Note>

      <Image src={screen3} alt="Paire de tuyaux" width="370px" height="370px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

      <Note type="tip" title="💡 Astuce">
        <p>Comment faire pour que les tuyaux soient plus ou moins grands en fonction de l’endroit où se trouve l’espace ?<br/><br/>
      Si l’espace est tout en bas, le tuyau du haut doit être plus grand que celui du bas, non ?<br/>

      L’astuce, c’est qu’on affiche toujours de très grands tuyaux, qui dépassent de l’écran.<br/>
      Comme ça, pas besoin de redimensionner les tuyaux : on les fait dépasser en haut et en bas du canvas.<br/>
      Le joueur ne verra rien, car tout ce qui dépasse du canvas n’est pas affiché !</p>
      </Note>
      <Image src={screen4} alt="Paire de tuyaux" width="370px" height="370px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

      <Separator type="text" text="Affichage des tuyaux" color="gray-darker" size="md" />
      <SectionTitle>🎨 3/ Affichage de notre paire de tuyaux</SectionTitle>

      <p>Une fois la paire de tuyaux définie, on l'affiche.</p>
      <p>On commence par charger les deux images dans <InlineCode>preload()</InlineCode> :</p>

      <CodeBlock language="javascript">
{`imageTuyauHaut = loadImage("tuyau-haut.png");
imageTuyauBas = loadImage("tuyau-bas.png");`}
      </CodeBlock>

      <p>En p5.js, on affiche une image avec :</p>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>image(nom, x, y)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Affiche une image<br/>- Paramètre nom : nom de l'image (sous forme de texte)<br/>- Paramètre x, y : position de l'image sur le canvas</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>



      <Note type="info" title="📍 Point de référence">
        <p>Le coin supérieur gauche de l'image est l'origine, c'est le point de pivot.</p>
      </Note>

      <Image src={screen2} alt="Paire de tuyaux" width="200px" height="200px" borderRadius="xl" shadow={true} border={false} className="object-contain" />

      <p>Maintenant, affichons le tuyau du haut en fonction des coordonnées de la paire de tuyaux (x,y):</p>
      <CodeBlock language="javascript">
{`image(imageTuyauHaut, x, y - imageTuyauHaut.height);`}
      </CodeBlock>
      <p>👉 Cela affiche imageTuyauHaut en x, et en y - hauteur de l’image, car le y de PaireTuyaux représente le bas du tuyau.</p>

      <p>Pour afficher le tuyau du bas, on fait la même chose, mais en y + espace :</p>
      <CodeBlock language="javascript">
{`image(imageTuyauBas, x, y + tuyauEspace);`}
      </CodeBlock>
      <p>👉 Cela affiche imageTuyauBas en x, On affiche imageTuyauBas en x, et en y + l’espace.</p>


      <p>Pour simplifier, on crée une fonction :</p>
      <CodeBlock language="javascript">
{`function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height);
  image(imageTuyauBas, t.x, t.y + tuyauEspace);
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : Afficher une paire de tuyaux en 300, 100">
          - Crée deux variables globales pour stocker les images :<br/>
        <CodeBlock language="javascript">
{`let imageTuyauHaut;
let imageTuyauBas;`}
        </CodeBlock>
         - Crée une variable globale pour définir l'espace entre les tuyaux :<br/>
        <CodeBlock language="javascript">
{`let tuyauEspace = 130;`}
        </CodeBlock>

      
          - Charge les deux images <InlineCode>tuyau-haut.png</InlineCode> et <InlineCode>tuyau-bas.png</InlineCode> dans <InlineCode>preload()</InlineCode><br/>
          - Dans <InlineCode>draw()</InlineCode>, crée un objet <InlineCode>paireTuyaux</InlineCode> avec :

          <InlineCode>x = 300</InlineCode>, <InlineCode>y = 100</InlineCode>, <InlineCode>franchi = false</InlineCode><br/>
          <CodeBlock language="javascript">
{`let paireTuyaux = {
  x: 300,
  y: 100,
  franchi: false
};`}
          </CodeBlock>

          - Appelle <InlineCode>afficherTuyaux(paireTuyaux)</InlineCode> dans <InlineCode>draw()</InlineCode><br/>
          - En bas du fichier, écris la fonction <InlineCode>afficherTuyaux(t)</InlineCode>.
          <CodeBlock language="javascript">
{`function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height); // Tuyau du haut
  image(imageTuyauBas, t.x, t.y + tuyauEspace); // Tuyau du bas
}`}
          </CodeBlock>

<br/>
        <Result>Tu devrais voir une paire de tuyaux au milieu de l'écran.</Result>
      </Note>

      <Separator type="text" text="Exploration" color="gray-darker" size="md" />
      <SectionTitle>🔍 Pour aller plus loin</SectionTitle>

      <p>Teste différentes valeurs pour <InlineCode>x</InlineCode> et <InlineCode>y</InlineCode> pour mieux comprendre leur effet :</p>
      <ul className="list-disc list-inside mb-4">
        <li><InlineCode>x = 0</InlineCode>, <InlineCode>y = 0</InlineCode></li>
        <li><InlineCode>y = 200</InlineCode>, <InlineCode>y = height</InlineCode></li>
        <li><InlineCode>x = width</InlineCode></li>
      </ul>

      <Note type="tip" title="❓ Pourquoi ne voit-on rien quand x = width ?">
        <p>Parce que <InlineCode>x</InlineCode> correspond au bord gauche de l'image. Si tu mets <InlineCode>x = width</InlineCode>, toute l'image est en dehors du canvas.</p>
        <p>👉 Essaie avec <InlineCode>x = width - 10</InlineCode> pour voir le début de l'image.</p>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Affichage d'une paire de tuyaux</SectionTitle>

      <CodeBlock language="javascript">
{`// Jeu 3 : Flappy Bird
let imageFond;
let imageTuyauHaut;
let imageTuyauBas;

let tuyauEspace = 130; // Espace entre les tuyaux

//--------------------------------- PRELOAD ------------------------------------------
function preload() {
  imageFond = loadImage("fond.png");
  imageTuyauHaut = loadImage("tuyau-haut.png");
  imageTuyauBas = loadImage("tuyau-bas.png");
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(600, 600);
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  background(imageFond);

  // Afficher les tuyaux
  let paireTuyaux = {
    x: 300,
    y: 100,
    franchi: false
  };
  afficherTuyaux(paireTuyaux);
}

//--------------------------------- CLIQUE ------------------------------------------
function mousePressed() {
  // Rien ici pour l'instant
}

//--------------------------------- AFFICHER TUYAUX ------------------------------------------
function afficherTuyaux(t) {
  image(imageTuyauHaut, t.x, t.y - imageTuyauHaut.height); // Tuyau du haut
  image(imageTuyauBas, t.x, t.y + tuyauEspace); // Tuyau du bas
}`}
      </CodeBlock>
    </>
  );
};

export default Chapitre2Content; 